import styled, { css } from 'styled-components';
import type { SignalCategory } from '../types';

export type CategoryKey = SignalCategory | 'all';

interface CategoryDef {
  key: CategoryKey;
  label: string;
}

interface CategoryFilterProps {
  categories: CategoryKey[];
  active: CategoryKey;
  onChange: (key: CategoryKey) => void;
}

const LABELS: Record<CategoryKey, string> = {
  all: 'All',
  sales: 'Sales',
  finance: 'Finance',
  operations: 'Operations',
  inventory: 'Inventory',
  customers: 'Customers'
};

export const CategoryFilter = ({
  categories,
  active,
  onChange
}: CategoryFilterProps) => {
  // Always render `all` first; everything else preserves the order
  // received from the parent (which preserves backend declaration order).
  const ordered: CategoryDef[] = [
    { key: 'all', label: LABELS.all },
    ...categories
      .filter((k) => k !== 'all')
      .map((k) => ({ key: k, label: LABELS[k] ?? k }))
  ];

  return (
    <Wrapper role="tablist">
      {ordered.map(({ key, label }) => (
        <Segment
          key={key}
          type="button"
          role="tab"
          aria-selected={active === key}
          $active={active === key}
          onClick={() => onChange(key)}
        >
          {label}
        </Segment>
      ))}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: inline-flex;
  padding: 4px;
  background: ${({ theme }) => theme.signals.chip_bg};
  border-radius: 999px;
  gap: 2px;
`;

const Segment = styled.button<{ $active: boolean }>`
  border: none;
  background: transparent;
  padding: 7px 16px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.signals.text_secondary};
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease;

  &:hover {
    color: ${({ theme }) => theme.signals.text_primary};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.signals.card_bg};
      color: ${theme.signals.text_primary};
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    `}
`;
